import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getScheduleEmployees, updateScheduleEmployeeVisibility, createBooking, getBookings, deleteBooking, updateBooking, getActivityTypes, batchBookings } from '../../services/scheduleService';
import { useCurrentUser } from '../../context/UserContext';
import { usePermissions } from '../../hooks/usePermission';
import FormModal from '../../components/forms/FormModal';
import DataTable from '../../components/forms/DataTable';
import Toast from '../../components/Toast';
import ConfirmDialog from '../../components/ConfirmDialog';
import DurationScrubber from '../../components/forms/DurationScrubber';
import ColorPicker from '../../components/forms/ColorPicker';
import CustomDropdown from '../../components/forms/CustomDropdown';
import SegmentedControl from '../../components/common/SegmentedControl';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import BatchBar from '../../components/common/BatchBar';
import CustomFieldRows from '../../components/common/CustomFieldRows';
import CustomFieldModal from '../../components/settings/CustomFieldModal';
import EntityListManager from '../../components/settings/EntityListManager';
import { getAssignedMilestones, getProjects, getMilestones } from '../../services/projectService';
import { getEmployees, getProjectsForEmployee } from '../../services/peopleService';
import ScheduleEmployeeDetailModal from '../../components/schedule/EmployeeDetailModal';
import { createTimeOffRequest, getTimeOffTypes, getTimeOffRequests, updateTimeOffRequest, cancelTimeOffRequest } from '../../services/timeOffService';
import { createActivityType, getActivityTypes as getActivityTypeList, updateActivityType, deleteActivityType } from '../../services/activityTypeService';
import { buildTimeOffCreatePayload, buildTimeOffUpdatePayload } from '../../utils/timeOffForm';
import { countLeaveDays, formatDays } from '../../utils/leaveDays';
import { addDays, formatDay, mondayOf, toISODate } from '../../utils/weekDates';
import { getCustomFields } from '../../services/customFieldService';
import { customCellRenderers, customSortAccessor, getFieldValue, plainCustomValue } from '../../utils/customFieldUtils';
import { useCurrency } from '../../hooks/useCurrency';
import { errorMessage } from '../../utils/errorMessage';
import ExportDialog from '../../components/common/ExportDialog';
import { toggleSort } from '../../utils/sortRows';
import useMinimumLoader from '../../hooks/useMinimumLoader';
import useTableColumns from '../../hooks/useTableColumns';
import useSelection from '../../hooks/useSelection';
import useBatchRunner from '../../hooks/useBatchRunner';
import styles from './Schedule.module.css';
import ui from './DashboardUI.module.css';
import sch from './schedule/ScheduleUI.module.css';
import ScheduleGrid from './schedule/ScheduleGrid';
import BookingChecks from './schedule/BookingChecks';
import { fmtHours } from './timesheets/timesheetsConfig';
import {
  BOOKING_EXPORT, DEFAULT_FILTERS, LEAVE_EXPORT, LIST_COLUMNS, RANGES, STORAGE_KEY, TYPE_LABEL,
  buildGroups, listRows, moveDates, normalizeBooking, normalizeLeave, selectedBookingIds, sortList, withDates,
} from './schedule/scheduleConfig';

const VIEW_KEY = 'scheduleView';
const loadView = () => {
  try {
    return JSON.parse(localStorage.getItem(VIEW_KEY)) || {};
  } catch {
    return {};
  }
};

const blankBooking = (over = {}) => ({
  bookingId: null,
  employeeId: '',
  resourceId: '',
  startDate: '',
  endDate: '',
  projectId: '',
  projectName: '',
  projectCompanyName: '',
  milestoneId: '',
  activityTypeId: '',
  hoursPerDay: 8,
  description: '',
  isTimeSpecific: false,
  startTime: '09:00',
  endTime: '17:00',
  customFields: {},
  ...over,
});

const clock = (iso) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const bookingToForm = (b) => blankBooking({
  bookingId: b.id,
  employeeId: b.employee_id || '',
  resourceId: b.resource_id || '',
  startDate: b.start,
  endDate: b.end,
  projectId: b.project_id || '',
  projectName: b.project_name || '',
  projectCompanyName: b.company_name || '',
  milestoneId: b.milestone_id || '',
  activityTypeId: b.activity_type_id || '',
  hoursPerDay: Math.round(b.perDay * 100) / 100 || 8,
  description: b.notes || '',
  isTimeSpecific: !!b.is_time_specific,
  startTime: b.is_time_specific ? clock(b.start_datetime) : '09:00',
  endTime: b.is_time_specific ? clock(b.end_datetime) : '17:00',
  customFields: b.custom_fields || {},
});

const blankTimeOff = (over = {}) => ({
  timeOffId: null,
  timeOffTypeId: '',
  startDate: '',
  endDate: '',
  reason: '',
  employee_id: '',
  startHalfDay: false,
  endHalfDay: false,
  ...over,
});

const Schedule = () => {
  const currentUser = useCurrentUser();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const canViewAllSchedules = hasPermission('view_schedule');
  const canViewOwnSchedule = hasPermission('view_own_schedule');
  const canCreateBookings = hasPermission('create_bookings');
  const canEditBookings = hasPermission('edit_bookings');
  const canDeleteBookings = hasPermission('delete_bookings');
  const canViewTimeOff = hasPermission('timeoff:view:self') || hasPermission('timeoff:view:all') || hasPermission('timeoff:approve');
  const canEditOwnPendingTimeOff = hasPermission('timeoff:edit:self:pending');
  const canEditTeamPendingTimeOff = hasPermission('timeoff:edit:team:pending');
  const canEditApprovedTimeOff = hasPermission('timeoff:edit:approved');
  const canApproveTimeOff = hasPermission('timeoff:approve');
  const canManageProjectTeam = hasPermission('manage_project_team');
  const canManageCompanySettings = hasPermission('manage_company_settings');
  const { format: formatMoney } = useCurrency();
  const hasAnyTimeOffPermission = canViewTimeOff || canEditOwnPendingTimeOff || canEditTeamPendingTimeOff || canEditApprovedTimeOff || canApproveTimeOff;
  const isViewingOwnScheduleOnly = !canViewAllSchedules && canViewOwnSchedule;

  const today = toISODate(new Date());
  const saved = useMemo(loadView, []);
  const [weekStart, setWeekStart] = useState(mondayOf(today));
  const [range, setRange] = useState(saved.range || 2);
  const [view, setView] = useState(saved.view || 'grid');
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...saved.filters, search: '' });
  const [collapsed, setCollapsed] = useState({});

  const [people, setPeople] = useState([]);
  // The login the page knows has no employee id, so find the person row for it.
  const selfId = people.find((p) => p.status === 'active' && p.user_id === currentUser?.user_id)?.id || '';
  const [resources, setResources] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [leave, setLeave] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [toast, setToast] = useState(null);

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [activeTab, setActiveTab] = useState('booking');
  const [bookingForm, setBookingForm] = useState(blankBooking());
  const [timeOffForm, setTimeOffForm] = useState(blankTimeOff());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelTimeOffConfirm, setShowCancelTimeOffConfirm] = useState(false);
  const [leaveSummary, setLeaveSummary] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [batchActivity, setBatchActivity] = useState(null);
  const [showNewActivityTypeModal, setShowNewActivityTypeModal] = useState(false);
  const [newActivityTypeForm, setNewActivityTypeForm] = useState({ name: '', description: '', color: '#3b82f6' });
  const [showManageActivities, setShowManageActivities] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [visibilityEmployees, setVisibilityEmployees] = useState([]);
  const [isVisibilityLoading, setIsVisibilityLoading] = useState(false);
  const [employeeMilestones, setEmployeeMilestones] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [selectedEmployeeDetail, setSelectedEmployeeDetail] = useState(null);
  const [employeeDetailTab, setEmployeeDetailTab] = useState('overview');
  const [employeeAssignedProjects, setEmployeeAssignedProjects] = useState([]);
  const [isEmployeeDetailLoading, setIsEmployeeDetailLoading] = useState(false);
  const [isEmployeeProjectsLoading, setIsEmployeeProjectsLoading] = useState(false);
  const [sort, setSort] = useState(null);
  const [bookingFieldDefs, setBookingFieldDefs] = useState([]);
  const [showAddDetail, setShowAddDetail] = useState(false);

  const gridSelection = useSelection();
  const listSelection = useSelection();
  const columnState = useTableColumns(STORAGE_KEY, LIST_COLUMNS, bookingFieldDefs);
  const runBatch = useBatchRunner(setToast);
  const showToast = useCallback((message, type = 'success') => setToast({ message, type }), []);

  const dates = useMemo(() => Array.from({ length: range * 7 }, (_, i) => addDays(weekStart, i)), [weekStart, range]);
  const first = dates[0];
  const last = dates[dates.length - 1];

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, JSON.stringify({ range, view, filters: { ...filters, search: '' } }));
    } catch {
      // Storage unavailable: the view just is not remembered.
    }
  }, [range, view, filters]);

  // Links from the Dashboard and People arrive as /schedule?open=<name>.
  useEffect(() => {
    const open = new URLSearchParams(location.search).get('open');
    if (open) setFilters((f) => ({ ...f, search: open }));
  }, [location.search]);

  const setFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));

  // ---- Data ----
  const fetchRows = useCallback(async () => {
    if (!canViewAllSchedules && !canViewOwnSchedule) return;
    try {
      const data = await getScheduleEmployees();
      setPeople((data.employees || data || []).map((u) => ({
        id: u.employee_id,
        user_id: u.user_id,
        name: u.name,
        avatar_url: u.avatar_url,
        job_title: u.job_title,
        kind: u.kind,
        department: u.department,
        weekly_hours: Number(u.weekly_hours) || 0,
        status: u.status,
      })));
      setResources((data.resources || []).map((r) => ({ id: r.resource_id, name: r.name, type: r.type })));
    } catch (error) {
      showToast(errorMessage(error, 'Failed to fetch employees'), 'error');
    }
  }, [canViewAllSchedules, canViewOwnSchedule, showToast]);

  const reload = useCallback(async () => {
    try {
      const [b, l] = await Promise.all([
        getBookings(first, last),
        hasAnyTimeOffPermission ? getTimeOffRequests({ start_date: first, end_date: last, status: 'approved,pending' }) : [],
      ]);
      setBookings(b.map(normalizeBooking));
      setLeave(l.map(normalizeLeave));
    } catch (err) {
      showToast(errorMessage(err, 'Failed to fetch bookings'), 'error');
    }
  }, [first, last, hasAnyTimeOffPermission, showToast]);

  useEffect(() => {
    fetchRows().finally(() => setLoading(false));
  }, [fetchRows]);

  useEffect(() => { if (!loading) reload(); }, [reload, loading]);

  const loadBookingFields = useCallback(
    () => getCustomFields('booking').then((d) => setBookingFieldDefs(d.fields || [])).catch(() => setBookingFieldDefs([])),
    []
  );
  useEffect(() => { loadBookingFields(); }, [loadBookingFields]);

  useEffect(() => {
    getActivityTypes().then(setActivityTypes).catch((err) => showToast(errorMessage(err, 'Failed to fetch activities'), 'error'));
  }, [showToast]);

  useEffect(() => {
    if (!hasAnyTimeOffPermission) return;
    getTimeOffTypes().then(setTimeOffTypes).catch((err) => showToast(errorMessage(err, 'Failed to fetch time off types'), 'error'));
  }, [hasAnyTimeOffPermission, showToast]);

  // Projects the person is on, plus (for team managers) the rest; milestones follow the chosen project.
  useEffect(() => {
    const { employeeId, resourceId, projectId } = bookingForm;
    if (!showBookingModal) return;
    if (!employeeId && !resourceId) {
      setEmployeeMilestones([]);
      setAllProjects([]);
      return;
    }
    (async () => {
      try {
        const [allData, assigned] = await Promise.all([getProjects(), employeeId ? getProjectsForEmployee(employeeId) : null]);
        const assignedIds = new Set((assigned || []).map((p) => p.project_id?.toString()));
        // A resource-only booking adds nobody to a team, so every project counts as available.
        setAllProjects((allData?.projects || allData || []).map((p) => ({ ...p, isAssigned: !employeeId || assignedIds.has(p.project_id?.toString()) })));
        if (projectId) {
          const milestones = !employeeId || !assignedIds.has(projectId.toString())
            ? await getMilestones(projectId)
            : await getAssignedMilestones(projectId, employeeId);
          setEmployeeMilestones(milestones || []);
        } else setEmployeeMilestones([]);
      } catch (err) {
        showToast(errorMessage(err, 'Failed to fetch employee projects'), 'error');
        setEmployeeMilestones([]);
        setAllProjects([]);
      }
    })();
  }, [showBookingModal, bookingForm.employeeId, bookingForm.resourceId, bookingForm.projectId, showToast]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showBookingModal) setCompanyFilter('');
    else if (bookingForm.projectId) setCompanyFilter(bookingForm.projectCompanyName || '__none__');
  }, [showBookingModal, bookingForm.projectId, bookingForm.projectCompanyName]);

  // ---- Grid and list ----
  const groups = useMemo(
    () => buildGroups({ people, resources, bookings: bookings.filter((b) => b.employee_id || b.resource_id), leave, filters, dates }),
    [people, resources, bookings, leave, filters, dates]
  );
  const rows = useMemo(() => sortList(listRows(groups), sort, customSortAccessor(bookingFieldDefs)), [groups, sort, bookingFieldDefs]);

  const projectOptions = useMemo(() => {
    const seen = new Map();
    bookings.forEach((b) => b.project_id && seen.set(String(b.project_id), b.project_name));
    return [{ value: '', label: 'All Projects' }, ...[...seen].sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label }))];
  }, [bookings]);
  const departmentOptions = useMemo(() => [
    { value: '', label: 'All Departments' },
    ...[...new Set(people.map((p) => p.department).filter(Boolean))].sort().map((d) => ({ value: d, label: d })),
  ], [people]);

  const shiftWeek = (weeks) => setWeekStart((w) => addDays(w, weeks * 7));
  const clearSelections = () => { gridSelection.clear(); listSelection.clear(); };

  // ---- Opening the form ----
  const openNewBooking = (over = {}) => {
    const own = selfId;
    setBookingForm(blankBooking({ employeeId: own, startDate: today, endDate: today, ...over }));
    setTimeOffForm(blankTimeOff({
      employee_id: over.employeeId || own,
      timeOffTypeId: timeOffTypes[0] ? String(timeOffTypes[0].leave_type_id || timeOffTypes[0].id) : '',
      startDate: over.startDate || today,
      endDate: over.endDate || today,
    }));
    setActiveTab('booking');
    setShowBookingModal(true);
  };

  const createFromDrag = (row, start, end) => {
    if (!canCreateBookings) return showToast('You do not have permission to create bookings', 'error');
    return openNewBooking(row.kind === 'person' ? { employeeId: row.who.id, startDate: start, endDate: end } : { resourceId: row.who.id, employeeId: '', startDate: start, endDate: end });
  };

  const openBooking = (b) => {
    const own = b.employee_id && String(b.employee_id) === String(selfId);
    if (!own && !canEditBookings) return showToast('You do not have permission to edit this booking', 'error');
    setBookingForm(bookingToForm(b));
    setActiveTab('booking');
    setShowBookingModal(true);
    return undefined;
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setActiveTab('booking');
    setBookingForm(blankBooking());
    setTimeOffForm(blankTimeOff());
  };

  const openEmployeeDetailModal = useCallback(async (employee) => {
    setEmployeeDetailTab('overview');
    setSelectedEmployeeDetail({ ...employee, employee_id: employee.id });
    setEmployeeAssignedProjects([]);
    setIsEmployeeDetailLoading(true);
    setIsEmployeeProjectsLoading(true);
    try {
      const [allEmployeesData, assignedProjectsData] = await Promise.all([
        getEmployees().catch(() => null),
        getProjectsForEmployee(employee.id).catch(() => null),
      ]);
      const detailed = (allEmployeesData?.employees || []).find((emp) => String(emp.user_id) === String(employee.id));
      if (detailed) setSelectedEmployeeDetail((prev) => ({ ...prev, ...detailed }));
      setEmployeeAssignedProjects(Array.isArray(assignedProjectsData) ? assignedProjectsData : []);
    } finally {
      setIsEmployeeDetailLoading(false);
      setIsEmployeeProjectsLoading(false);
    }
  }, []);

  // ---- Saving ----
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!bookingForm.employeeId && !bookingForm.resourceId) return showToast('Please choose who to book', 'error');
    if (!bookingForm.projectId && !bookingForm.activityTypeId) return showToast('Please select a project and/or activity', 'error');

    const isOwnBooking = String(bookingForm.employeeId) === String(selfId);
    if (bookingForm.bookingId ? !canEditBookings : !canCreateBookings) {
      return showToast(`You do not have permission to ${bookingForm.bookingId ? 'edit' : 'create'} bookings`, 'error');
    }
    if (!bookingForm.bookingId && bookingForm.employeeId && !isOwnBooking && !canEditBookings) {
      return showToast('You do not have permission to create bookings for other employees', 'error');
    }

    try {
      const payload = {
        employee_id: bookingForm.employeeId || null,
        resource_id: bookingForm.resourceId || null,
        project_id: bookingForm.projectId || null,
        milestone_id: bookingForm.milestoneId || null,
        activity_type_id: bookingForm.activityTypeId || null,
        from_date: bookingForm.startDate,
        to_date: bookingForm.endDate,
        hours_per_day: bookingForm.hoursPerDay,
        description: bookingForm.description,
        custom_fields: bookingForm.customFields,
        is_time_specific: bookingForm.isTimeSpecific,
        start_time: bookingForm.startTime,
        end_time: bookingForm.endTime,
      };
      const result = bookingForm.bookingId ? await updateBooking(bookingForm.bookingId, payload) : await createBooking(payload);
      const verb = bookingForm.bookingId ? 'updated' : 'created';
      const personName = people.find((p) => String(p.id) === String(bookingForm.employeeId))?.name || 'they';
      showToast(result?.added_to_team ? `Booking ${verb} and ${personName} added to ${bookingForm.projectName || 'the project'}'s team` : `Booking ${verb} successfully`);
      await reload();
      closeBookingModal();
    } catch (error) {
      showToast(errorMessage(error, 'Failed to save booking'), 'error');
    }
    return undefined;
  };

  const handleTimeOffSubmit = async (e) => {
    e.preventDefault();
    if (!timeOffForm.timeOffTypeId || !timeOffForm.startDate || !timeOffForm.endDate) return showToast('Please fill in all required fields', 'error');
    try {
      if (timeOffForm.timeOffId) {
        await updateTimeOffRequest(timeOffForm.timeOffId, buildTimeOffUpdatePayload(timeOffForm));
        showToast('Time off request updated successfully');
      } else {
        await createTimeOffRequest(buildTimeOffCreatePayload(timeOffForm, selfId));
        showToast('Time off request submitted successfully');
      }
      await reload();
      closeBookingModal();
    } catch (error) {
      showToast(errorMessage(error, 'Failed to submit time off request'), 'error');
    }
    return undefined;
  };

  const handleCreateActivityType = async () => {
    if (!newActivityTypeForm.name.trim()) return showToast('Activity name is required.', 'error');
    try {
      const created = await createActivityType({ ...newActivityTypeForm, name: newActivityTypeForm.name.trim(), description: newActivityTypeForm.description.trim() });
      setActivityTypes(await getActivityTypes());
      setBookingForm((prev) => ({ ...prev, activityTypeId: created.activity_type_id }));
      setShowNewActivityTypeModal(false);
      setNewActivityTypeForm({ name: '', description: '', color: '#3b82f6' });
      showToast('Activity created successfully.');
    } catch (err) {
      showToast(errorMessage(err, 'Failed to create activity.'), 'error');
    }
    return undefined;
  };

  // ---- Moving, with Undo ----
  const moveBooking = async (item, targetRow, grabDate, dropDate) => {
    const { start, end } = moveDates(item, grabDate, dropDate);
    const sameRow = targetRow.kind === 'person' ? targetRow.who.id === item.employee_id : targetRow.who.id === item.resource_id;
    if (sameRow && start === item.start) return;
    if (!canEditBookings) { showToast('You do not have permission to move bookings', 'error'); return; }
    const previous = { employee_id: item.employee_id, resource_id: item.resource_id, start_datetime: item.start_datetime, end_datetime: item.end_datetime };
    const patch = { ...withDates(item, start, end), ...(targetRow.kind === 'person' ? { employee_id: targetRow.who.id } : { resource_id: targetRow.who.id }) };
    try {
      await updateBooking(item.id, patch);
      await reload();
      setToast({
        message: `Moved ${item.title} to ${targetRow.who.name}, ${formatDay(start)}${end === start ? '' : ` – ${formatDay(end)}`}`,
        type: 'success',
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await updateBooking(item.id, previous);
              await reload();
              setToast({ message: 'Move undone.', type: 'success' });
            } catch (err) {
              setToast({ message: errorMessage(err, 'Undo failed.'), type: 'error' });
            }
          },
        },
      });
    } catch (err) {
      showToast(errorMessage(err, 'Failed to move booking'), 'error');
    }
  };

  // ---- Batch changes ----
  const selectedRowKeys = gridSelection.selectedIds;
  const gridRows = groups.flatMap((g) => g.rows);
  const gridIds = selectedBookingIds(groups, selectedRowKeys);
  const listIds = [...new Set(rows.filter((r) => listSelection.selectedIds.has(r.key)).map((r) => r.id))];
  const tickedRows = gridRows.filter((r) => selectedRowKeys.has(r.key));

  const shiftBookings = (ids, days) => runBatch(
    () => batchBookings({ ids, shift_days: days }),
    reload,
    (r) => `Moved ${r.updated.length} booking${r.updated.length === 1 ? '' : 's'} ${Math.abs(days) === 7 ? `a week ${days > 0 ? 'forward' : 'back'}` : `${Math.abs(days)} days ${days > 0 ? 'forward' : 'back'}`}.`,
    () => batchBookings({ ids, shift_days: -days }),
  );

  const copyWeek = () => runBatch(
    () => batchBookings({
      copy_from: weekStart,
      copy_to: addDays(weekStart, 7),
      employee_ids: tickedRows.filter((r) => r.kind === 'person').map((r) => r.who.id),
      resource_ids: tickedRows.filter((r) => r.kind === 'resource').map((r) => r.who.id),
    }),
    reload,
    (r) => `Copied ${r.created.length} booking${r.created.length === 1 ? '' : 's'} to next week.`,
    (r) => batchBookings({ ids: r.created, delete: true }),
  );

  const deleteBookings = (ids) => runBatch(
    () => batchBookings({ ids, delete: true }),
    () => { clearSelections(); return reload(); },
    (r) => `Deleted ${r.deleted.length} booking${r.deleted.length === 1 ? '' : 's'}.`,
    (r) => batchBookings({ restore_deleted: r.deleted }),
  );

  const askDelete = (ids) => setConfirm({
    message: `Delete ${ids.length} booking${ids.length === 1 ? '' : 's'}? You can undo this right after.`,
    confirmText: 'Delete',
    onConfirm: () => { setConfirm(null); deleteBookings(ids); },
  });

  const changeActivity = (ids, activityId) => runBatch(
    () => batchBookings({ ids, activity_type_id: activityId || null }),
    reload,
    (r) => `Changed the activity on ${r.updated.length} booking${r.updated.length === 1 ? '' : 's'}.`,
    (r) => batchBookings({ restore_activity: r.previous }),
  );

  // ---- Export ----
  const [exporting, setExporting] = useState(null);
  const bookingExportColumns = [
    ...BOOKING_EXPORT.map((c) => ({ ...c, key: c.label, visible: true })),
    ...bookingFieldDefs.map((f) => ({ key: `cf_${f.field_id}`, label: f.name, visible: true, value: (r) => plainCustomValue(f, getFieldValue(r, f)) })),
  ];
  // Leave for the people on screen rides along as a second sheet in Excel.
  const leaveSheet = () => {
    const shown = new Set(gridRows.filter((r) => r.kind === 'person').map((r) => r.who.id));
    return [{ name: 'Leave', columns: LEAVE_EXPORT, rows: leave.filter((l) => shown.has(l.employee_id)) }];
  };

  const openVisibility = async () => {
    setShowVisibilityModal(true);
    setIsVisibilityLoading(true);
    try {
      const data = await getScheduleEmployees({ include_hidden: true });
      setVisibilityEmployees((data.employees || data || []).map((e) => ({ employee_id: e.employee_id, name: e.name, job_title: e.job_title, hide_on_schedule: !!e.hide_on_schedule })));
    } catch (error) {
      showToast(errorMessage(error, 'Failed to fetch employee visibility'), 'error');
    } finally {
      setIsVisibilityLoading(false);
    }
  };

  const toggleVisibility = async (employeeId, hidden) => {
    try {
      await updateScheduleEmployeeVisibility(employeeId, hidden);
      await Promise.all([fetchRows(), openVisibility()]);
    } catch (error) {
      showToast(errorMessage(error, 'Failed to update schedule visibility'), 'error');
    }
  };

  const menuActions = [
    { label: 'Export…', onClick: () => setExporting({ scope: 'shown', ids: [] }) },
    ...(canCreateBookings ? [{ label: 'Manage activities', onClick: () => setShowManageActivities(true) }] : []),
    ...(canViewAllSchedules && canEditBookings ? [{ label: 'Who appears on the schedule', onClick: openVisibility }] : []),
  ];

  // ---- Leave ----
  const openLeave = (l) => setLeaveSummary(l);

  const editLeave = (l) => {
    const own = String(l.employee_id) === String(selfId);
    const allowed = canApproveTimeOff || (own && canEditOwnPendingTimeOff && !l.approved) || (canEditApprovedTimeOff && l.approved);
    if (!allowed) return showToast('You do not have permission to edit this time off request', 'error');
    setTimeOffForm(blankTimeOff({
      employee_id: l.employee_id,
      timeOffId: l.leave_request_id,
      timeOffTypeId: String(l.time_off_type_id),
      startDate: l.start,
      endDate: l.end,
      reason: l.reason || '',
      startHalfDay: !!l.start_half_day,
      endHalfDay: !!l.end_half_day,
    }));
    setLeaveSummary(null);
    setActiveTab('timeOff');
    setShowBookingModal(true);
    return undefined;
  };

  // ---- Form pieces ----
  const whoSections = [
    { label: 'People', items: people.map((p) => ({ value: `e${p.id}`, label: p.name + (p.job_title ? ` (${p.job_title})` : '') })) },
    { label: 'Resources', items: resources.map((r) => ({ value: `r${r.id}`, label: r.name })) },
  ];
  const whoValue = bookingForm.employeeId ? `e${bookingForm.employeeId}` : bookingForm.resourceId ? `r${bookingForm.resourceId}` : '';
  const personName = people.find((e) => String(e.id) === String(bookingForm.employeeId))?.name || 'them';

  const renderTimeOffDatesField = () => {
    const sameDay = timeOffForm.startDate && timeOffForm.startDate === timeOffForm.endDate;
    const days = countLeaveDays(timeOffForm.startDate, timeOffForm.endDate, timeOffForm.startHalfDay, timeOffForm.endHalfDay);
    const setT = (patch) => setTimeOffForm((prev) => ({ ...prev, ...patch }));
    return (
      <div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Start Date</label>
            <input type="date" value={timeOffForm.startDate} onChange={(e) => setT({ startDate: e.target.value })} style={{ width: '100%' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>End Date</label>
            <input type="date" value={timeOffForm.endDate} onChange={(e) => setT({ endDate: e.target.value })} style={{ width: '100%' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '0.85rem', color: '#374151' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input type="checkbox" checked={!!timeOffForm.startHalfDay} onChange={(e) => setT({ startHalfDay: e.target.checked })} />
            {sameDay ? 'Half day' : 'First day is a half-day'}
          </label>
          {!sameDay && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input type="checkbox" checked={!!timeOffForm.endHalfDay} onChange={(e) => setT({ endHalfDay: e.target.checked })} />
              Last day is a half-day
            </label>
          )}
        </div>
        {timeOffForm.startDate && timeOffForm.endDate && (
          <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#6b7280' }}>
            {formatDays(days)} working day{days === 1 ? '' : 's'} (weekends excluded)
          </div>
        )}
      </div>
    );
  };

  const projectField = () => {
    const assignedIn = (list) => list.filter((p) => p.isAssigned);
    const open = allProjects.filter((p) => p.isAssigned || p.name?.toLowerCase() !== 'completed');
    const companies = new Set();
    let hasNone = false;
    open.filter((p) => p.isAssigned || canManageProjectTeam).forEach((p) => (p.client_company ? companies.add(p.client_company) : (hasNone = true)));
    const companyOptions = [{ value: '', label: 'All companies' }, ...[...companies].sort().map((n) => ({ value: n, label: n })), ...(hasNone ? [{ value: '__none__', label: 'No company' }] : [])];
    const inCompany = !companyFilter ? open : companyFilter === '__none__' ? open.filter((p) => !p.client_company) : open.filter((p) => p.client_company === companyFilter);
    const assignedInFilter = assignedIn(inCompany);
    const othersInFilter = canManageProjectTeam ? inCompany.filter((p) => !p.isAssigned) : [];
    const noWho = !bookingForm.employeeId && !bookingForm.resourceId;
    const projectOption = (project) => ({ value: String(project.project_id), label: `${project.project_name || 'Untitled project'} (${project.client_company || 'No Client'})` });
    const inList = bookingForm.projectId && allProjects.some((p) => String(p.project_id) === String(bookingForm.projectId));
    const placeholder = allProjects.length === 0 ? 'No projects available' : noWho ? 'Select who first' : 'Select project...';
    const labelStyle = { display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div>
          <label style={labelStyle}>Company</label>
          <CustomDropdown
            options={companyOptions}
            value={companyFilter}
            onChange={(val) => {
              setCompanyFilter(val);
              setBookingForm((prev) => ({ ...prev, projectId: '', projectName: '', projectCompanyName: '', milestoneId: '' }));
            }}
            disabled={noWho || companyOptions.length === 1}
            fullWidth
          />
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Project</label>
            <CustomDropdown
              sections={[
                {
                  items: [
                    { value: '', label: placeholder },
                    ...(bookingForm.projectId && bookingForm.projectName && !inList
                      ? [{ value: String(bookingForm.projectId), label: `${bookingForm.projectName} (${bookingForm.projectCompanyName || 'No Client'}) — Completed` }]
                      : []),
                    ...assignedInFilter.map(projectOption),
                  ],
                },
                ...(othersInFilter.length > 0 ? [{ label: `Other projects — saving adds ${personName} to the team`, items: othersInFilter.map(projectOption) }] : []),
              ]}
              value={bookingForm.projectId ? String(bookingForm.projectId) : ''}
              onChange={(val) => {
                const project = allProjects.find((p) => String(p.project_id) === val);
                setBookingForm((prev) => ({ ...prev, projectId: val, projectName: project?.project_name || prev.projectName, projectCompanyName: project?.client_company || '', milestoneId: '' }));
              }}
              disabled={noWho || assignedInFilter.length + othersInFilter.length === 0}
              fullWidth
            />
            {bookingForm.employeeId && inList && !allProjects.find((p) => String(p.project_id) === String(bookingForm.projectId))?.isAssigned && (
              <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#185FA5' }}>Saving adds {personName} to this project's team.</p>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Activity</label>
            <CustomDropdown
              options={[{ value: '', label: 'None' }, ...activityTypes.map((t) => ({ value: String(t.activity_type_id), label: t.name }))]}
              value={bookingForm.activityTypeId ? String(bookingForm.activityTypeId) : ''}
              onChange={(val) => setBookingForm((prev) => ({ ...prev, activityTypeId: val }))}
              actions={[{ label: '➕ Add New Activity...', onClick: () => setShowNewActivityTypeModal(true) }]}
              fullWidth
            />
          </div>
        </div>
      </div>
    );
  };

  const personOptions = [{ value: '', label: 'Select employee...' }, ...people.map((e) => ({ value: e.id, label: e.name + (e.job_title ? ` (${e.job_title})` : '') }))];

  const bookingFields = [
    ...(!isViewingOwnScheduleOnly && canViewAllSchedules && canEditBookings ? [{
      label: 'Who',
      type: 'custom',
      render: () => (
        <CustomDropdown
          sections={whoSections}
          value={whoValue}
          placeholder="Select person or resource..."
          onChange={(val) => setBookingForm((prev) => ({
            ...prev,
            employeeId: val.startsWith('e') ? Number(val.slice(1)) : '',
            resourceId: val.startsWith('r') ? Number(val.slice(1)) : (val.startsWith('e') ? prev.resourceId : ''),
            projectId: '',
            projectName: '',
            milestoneId: '',
          }))}
          fullWidth
        />
      ),
    }] : []),
    {
      label: 'Project Dates',
      type: 'custom',
      render: () => (
        <div style={{ display: 'flex', gap: '16px' }}>
          {[['startDate', 'Start Date'], ['endDate', 'End Date']].map(([key, label]) => (
            <div key={key} style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>{label}</label>
              <input type="date" value={bookingForm[key]} onChange={(e) => setBookingForm((prev) => ({ ...prev, [key]: e.target.value }))} style={{ width: '100%' }} />
            </div>
          ))}
        </div>
      ),
    },
    { label: 'Project/Activity *', type: 'custom', render: projectField },
    ...(bookingForm.employeeId ? [{
      label: 'Book Resource (optional)',
      type: 'select',
      value: bookingForm.resourceId,
      onChange: (e) => setBookingForm((prev) => ({ ...prev, resourceId: e.target.value })),
      options: [{ value: '', label: 'No resource' }, ...resources.map((r) => ({ value: r.id, label: r.name }))],
    }] : []),
    ...(employeeMilestones.length > 0 ? [{
      label: 'Milestone',
      type: 'select',
      required: false,
      value: bookingForm.milestoneId,
      onChange: (e) => setBookingForm((prev) => ({ ...prev, milestoneId: e.target.value })),
      options: [{ value: '', label: 'None' }, ...employeeMilestones.map((m) => ({ value: m.milestone_id, label: m.milestone_name }))],
    }] : []),
    {
      type: 'custom',
      render: () => (
        <DurationScrubber
          hours={bookingForm.hoursPerDay}
          startTime={bookingForm.startTime}
          endTime={bookingForm.endTime}
          startOn={bookingForm.isTimeSpecific}
          onStartOnChange={(isOn) => setBookingForm((prev) => ({ ...prev, isTimeSpecific: isOn }))}
          onChange={({ hours, startTime, endTime }) => setBookingForm((prev) => ({ ...prev, hoursPerDay: hours, startTime, endTime }))}
        />
      ),
    },
    { type: 'custom', render: () => <BookingChecks form={bookingForm} canSeeLeave={hasAnyTimeOffPermission} /> },
    { label: 'Description', type: 'textarea', value: bookingForm.description, onChange: (e) => setBookingForm((prev) => ({ ...prev, description: e.target.value })) },
    ...(bookingFieldDefs.length > 0 ? [{
      type: 'custom',
      render: () => (
        <CustomFieldRows
          fields={bookingFieldDefs}
          entity={{ custom_fields: bookingForm.customFields }}
          formatMoney={formatMoney}
          onSave={(field, value) => setBookingForm((prev) => ({ ...prev, customFields: { ...prev.customFields, [field.field_id]: value } }))}
        />
      ),
    }] : []),
  ];

  const timeOffFields = [
    ...(!isViewingOwnScheduleOnly && canViewAllSchedules && canEditTeamPendingTimeOff ? [{
      label: 'Employee',
      type: 'select',
      value: timeOffForm.employee_id,
      onChange: (e) => setTimeOffForm((prev) => ({ ...prev, employee_id: e.target.value })),
      options: personOptions,
    }] : []),
    {
      label: 'Time Off Type',
      type: 'select',
      required: true,
      value: timeOffForm.timeOffTypeId,
      disabled: timeOffTypes.length === 0,
      onChange: (e) => setTimeOffForm((prev) => ({ ...prev, timeOffTypeId: e.target.value })),
      options: timeOffTypes.length === 0
        ? [{ value: '', label: 'Loading time off types...' }]
        : timeOffTypes.map((t) => ({ value: String(t.leave_type_id || t.id || ''), label: t.name || 'Unnamed' })),
    },
    { label: 'Dates', type: 'custom', render: renderTimeOffDatesField },
    { label: 'Reason', type: 'textarea', value: timeOffForm.reason, onChange: (e) => setTimeOffForm((prev) => ({ ...prev, reason: e.target.value })) },
    {
      type: 'custom',
      render: () => (
        <p className={sch.formNote}>
          This sends a request to <a href="/dashboard/leave-manager" onClick={(e) => { e.preventDefault(); navigate('/dashboard/leave-manager'); }}>Leave</a> for approval. It shows here as a dashed block until it is approved.
        </p>
      ),
    },
  ];

  // ---- List cells ----
  const listCells = {
    who: (r) => <span><strong>{r.who}</strong>{r.resource_name && r.employee_id && <span className={sch.subDates}>with {r.resource_name}</span>}</span>,
    project: (r) => <span className={sch.projectChip}><i style={{ background: r.project_color || '#94a3b8' }} />{r.title}</span>,
    activity: (r) => r.activity_type_name || '—',
    dates: (r) => (
      <span>
        {formatDay(r.start)}{r.end === r.start ? '' : ` – ${formatDay(r.end)}`}
        <span className={r.clash || r.leaveClash ? sch.subBad : sch.subDates}>
          {r.clash ? `Clashes with ${r.clash}` : r.leaveClash ? 'Clashes with leave' : `${r.days} working day${r.days === 1 ? '' : 's'}`}
        </span>
      </span>
    ),
    hours: (r) => fmtHours(r.perDay),
    total: (r) => <span className={sch.total}>{fmtHours(r.total)}</span>,
    client: (r) => r.company_name || '—',
    milestone: (r) => r.milestone_name || '—',
    notes: (r) => r.notes || '—',
    ...customCellRenderers(bookingFieldDefs, formatMoney),
  };

  const groupFn = filters.groupBy ? (r) => r.group || 'Bookings' : null;
  const rangeLabel = `${formatDay(first)} – ${formatDay(last, true)}`;
  const nothing = groups.length === 0;

  return (
    <div className={ui.fadeInContent}>
      {toast && <Toast message={toast.message} type={toast.type} action={toast.action} onClose={() => setToast(null)} />}

      {showLoading ? (
        <div className={ui.pageContainer}>
          <div className={ui.toolbarSkeleton}>
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className={`${ui.skeletonBlock} ${ui.toolbarSkeletonPill}`} />)}
          </div>
          <div className={ui.tableSkeleton}>
            <div className={`${ui.skeletonBlock} ${ui.tableSkeletonHeader}`} />
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className={`${ui.skeletonBlock} ${ui.tableSkeletonRow}`} />)}
          </div>
        </div>
      ) : (
        <>
          <div className={ui.pageHeader}>
            <div className={ui.pageHeaderLeft} style={{ alignItems: 'center' }}>
              <h1 className={ui.pageTitle}>{isViewingOwnScheduleOnly ? 'My Schedule' : 'Schedule'}</h1>
              <button type="button" className={sch.pagerButton} aria-label="Previous week" onClick={() => shiftWeek(-1)}>‹</button>
              <span className={sch.range}>{rangeLabel}</span>
              <button type="button" className={sch.pagerButton} aria-label="Next week" onClick={() => shiftWeek(1)}>›</button>
              <button type="button" className={sch.pagerButton} onClick={() => setWeekStart(mondayOf(today))}>Today</button>
            </div>
            <div className={ui.pageHeaderRight}>
              <SegmentedControl ariaLabel="Weeks shown" options={RANGES} value={range} onChange={setRange} />
              <SegmentedControl ariaLabel="Grid or list" options={[{ value: 'grid', label: 'Grid' }, { value: 'list', label: 'List' }]} value={view} onChange={(v) => { clearSelections(); setView(v); }} />
              <RowActionsMenu actions={menuActions} />
              {(canCreateBookings || hasAnyTimeOffPermission) && (
                <button type="button" className={ui.addButton} onClick={() => openNewBooking()}>
                  <span className={ui.icon}>＋</span> Booking
                </button>
              )}
            </div>
          </div>

          <div className={ui.tableToolbar}>
            <input
              type="text"
              className={ui.toolbarSearch}
              placeholder="Search people, projects, notes…"
              aria-label="Search"
              value={filters.search}
              onChange={(e) => setFilter({ search: e.target.value })}
            />
            <CustomDropdown toolbar options={[{ value: '', label: 'All People' }, ...Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label }))]} value={filters.type} onChange={(v) => setFilter({ type: v })} />
            <CustomDropdown toolbar options={projectOptions} value={filters.project} onChange={(v) => setFilter({ project: v })} />
            <CustomDropdown toolbar options={departmentOptions} value={filters.department} onChange={(v) => setFilter({ department: v })} />
            <CustomDropdown
              toolbar
              options={[{ value: '', label: 'No Grouping' }, { value: 'type', label: 'Group by Type' }, { value: 'department', label: 'Group by Department' }]}
              value={filters.groupBy}
              onChange={(v) => setFilter({ groupBy: v })}
            />
            <button type="button" className={ui.toolbarReset} onClick={() => setFilters(DEFAULT_FILTERS)}>Clear</button>
          </div>

          {view === 'grid' ? (
            <>
              <BatchBar count={gridSelection.selectedIds.size} onClear={gridSelection.clear}>
                {canEditBookings && gridIds.length > 0 && (
                  <>
                    <button type="button" onClick={() => shiftBookings(gridIds, 7)}>Move a week forward</button>
                    <button type="button" onClick={() => shiftBookings(gridIds, -7)}>Move a week back</button>
                  </>
                )}
                {canCreateBookings && gridIds.length > 0 && <button type="button" onClick={copyWeek}>Copy to next week</button>}
                {canDeleteBookings && gridIds.length > 0 && <button type="button" onClick={() => askDelete(gridIds)}>Clear bookings in view</button>}
                <button type="button" onClick={() => setExporting({ scope: 'selected', ids: gridIds })}>Export</button>
              </BatchBar>
              {nothing ? (
                <p style={{ padding: '24px 20px', color: '#64748b' }}>{people.length === 0 ? 'No one to schedule yet.' : 'No one matches these filters.'}</p>
              ) : (
                <ScheduleGrid
                  groups={groups}
                  dates={dates}
                  today={today}
                  selection={gridSelection}
                  collapsed={collapsed}
                  onToggleCollapse={(k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }))}
                  canCreate={canCreateBookings}
                  canMove={canEditBookings}
                  onCreate={createFromDrag}
                  onMove={moveBooking}
                  onBooking={openBooking}
                  onLeave={openLeave}
                  onPerson={openEmployeeDetailModal}
                />
              )}
            </>
          ) : (
            <>
              <BatchBar count={listSelection.selectedIds.size} onClear={listSelection.clear}>
                {canEditBookings && (
                  <>
                    <button type="button" onClick={() => setBatchActivity('')}>Change activity</button>
                    <button type="button" onClick={() => shiftBookings(listIds, 7)}>Shift a week forward</button>
                    <button type="button" onClick={() => shiftBookings(listIds, -7)}>Shift a week back</button>
                  </>
                )}
                {canDeleteBookings && <button type="button" onClick={() => askDelete(listIds)}>Delete</button>}
                <button type="button" onClick={() => setExporting({ scope: 'selected', ids: listIds })}>Export</button>
              </BatchBar>
              {rows.length === 0 ? (
                <p style={{ padding: '24px 20px', color: '#64748b' }}>No bookings in this range.</p>
              ) : (
                <div className={sch.listWrap}>
                  <DataTable
                    columns={columnState.columns}
                    data={rows}
                    storageKey={STORAGE_KEY}
                    groupedBy={groupFn}
                    getGroupColor={() => '#85B7EB'}
                    collapsedGroups={collapsed}
                    toggleGroupCollapse={(g) => setCollapsed((c) => ({ ...c, [g]: !c[g] }))}
                    sortColumn={sort?.key}
                    sortDirection={sort?.dir || 'asc'}
                    onSort={(key) => setSort((s) => toggleSort(s, key))}
                    onRowClick={openBooking}
                    onAddDetail={canManageCompanySettings ? () => setShowAddDetail(true) : null}
                    onToggleColumn={columnState.toggleColumn}
                    onMoveColumn={columnState.moveColumn}
                    selectable
                    selectedIds={listSelection.selectedIds}
                    getRowId={(r) => r.key}
                    onToggleRow={listSelection.toggleRow}
                    onToggleGroup={listSelection.toggleGroup}
                    onToggleAll={(checked) => listSelection.toggleAll(rows.map((r) => r.key), checked)}
                    customCellRender={listCells}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      {showBookingModal && (
        <FormModal
          title={activeTab === 'booking' ? (bookingForm.bookingId ? 'Edit Booking' : 'Add Booking') : (timeOffForm.timeOffId ? 'Edit Time Off Request' : 'Request Time Off')}
          onCancel={closeBookingModal}
          onSubmit={activeTab === 'booking' ? handleBookingSubmit : handleTimeOffSubmit}
          tabs={[{ key: 'booking', label: 'Booking' }, ...(hasAnyTimeOffPermission ? [{ key: 'timeOff', label: 'Time Off' }] : [])]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          fields={activeTab === 'booking' ? bookingFields : timeOffFields}
          error={null}
          footerActions={
            activeTab === 'booking'
              ? !!bookingForm.bookingId && canDeleteBookings && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <button type="button" className={styles.buttonDanger} onClick={() => setShowDeleteConfirm(true)}>Delete Booking</button>
                </div>
              )
              : timeOffForm.timeOffId && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <button type="button" className={styles.buttonDanger} onClick={() => setShowCancelTimeOffConfirm(true)}>Cancel Request</button>
                </div>
              )
          }
        />
      )}

      {selectedEmployeeDetail && (
        <ScheduleEmployeeDetailModal
          employee={selectedEmployeeDetail}
          activeTab={employeeDetailTab}
          onTabChange={setEmployeeDetailTab}
          isDetailLoading={isEmployeeDetailLoading}
          isProjectsLoading={isEmployeeProjectsLoading}
          assignedProjects={employeeAssignedProjects}
          onClose={() => { setSelectedEmployeeDetail(null); setEmployeeAssignedProjects([]); }}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmDialog
          message="Are you sure you want to delete this booking? This action cannot be undone."
          onConfirm={async () => {
            try {
              await deleteBooking(bookingForm.bookingId);
              closeBookingModal();
              setShowDeleteConfirm(false);
              await reload();
              showToast('Booking deleted successfully');
            } catch (err) {
              showToast(errorMessage(err, 'Failed to delete booking'), 'error');
            }
          }}
          onCancel={() => setShowDeleteConfirm(false)}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}

      {showCancelTimeOffConfirm && (
        <ConfirmDialog
          message="Are you sure you want to cancel this time off request? This action cannot be undone."
          onConfirm={async () => {
            try {
              await cancelTimeOffRequest(timeOffForm.timeOffId);
              setShowCancelTimeOffConfirm(false);
              closeBookingModal();
              await reload();
              showToast('Time off request cancelled successfully');
            } catch (err) {
              showToast(errorMessage(err, 'Failed to cancel time off request'), 'error');
            }
          }}
          onCancel={() => setShowCancelTimeOffConfirm(false)}
          confirmText="Cancel Request"
          cancelText="Cancel"
        />
      )}

      {exporting && (
        <ExportDialog
          title="Export bookings"
          filename={`schedule-${first}-to-${last}`}
          scopes={[
            { value: 'shown', label: 'Bookings shown', rows },
            ...(exporting.ids.length ? [{ value: 'selected', label: 'Ticked bookings', rows: rows.filter((r) => exporting.ids.includes(r.id)) }] : []),
          ]}
          defaultScope={exporting.scope}
          columns={bookingExportColumns}
          groups={[{ value: 'group', label: 'Schedule group', get: (r) => r.group }]}
          defaultGroup={filters.groupBy ? 'group' : ''}
          extraSheets={leaveSheet}
          onExported={(count, kind) => showToast(`${count} ${count === 1 ? 'booking' : 'bookings'} exported${kind === 'csv' ? ' as CSV' : ''}.`)}
          onClose={() => setExporting(null)}
        />
      )}

      {confirm && <ConfirmDialog message={confirm.message} confirmText={confirm.confirmText} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}

      {leaveSummary && (
        <FormModal
          title={leaveSummary.approved ? 'Approved leave' : 'Leave waiting for approval'}
          onCancel={() => setLeaveSummary(null)}
          onSubmit={(e) => { e.preventDefault(); navigate('/dashboard/leave-manager'); }}
          submitLabel="Open in Leave"
          fields={[{
            type: 'custom',
            render: () => (
              <dl className={sch.leaveFacts}>
                <dt>Who</dt><dd>{leaveSummary.employee_name}</dd>
                <dt>Type</dt><dd>{leaveSummary.type_name}</dd>
                <dt>Dates</dt><dd>{formatDay(leaveSummary.start)}{leaveSummary.end === leaveSummary.start ? '' : ` – ${formatDay(leaveSummary.end, true)}`}</dd>
                <dt>Working days</dt><dd>{formatDays(countLeaveDays(leaveSummary.start, leaveSummary.end, leaveSummary.start_half_day, leaveSummary.end_half_day))}</dd>
                <dt>Status</dt><dd>{leaveSummary.approved ? 'Approved' : 'Pending'}</dd>
                {leaveSummary.reason && <><dt>Reason</dt><dd>{leaveSummary.reason}</dd></>}
              </dl>
            ),
          }, {
            type: 'custom',
            render: () => (
              <p className={sch.formNote}>
                {leaveSummary.approved ? 'Leave is managed on the Leave page.' : 'Approve or deny this request on the Leave page.'}
                {' '}<a href="/dashboard/leave-manager" onClick={(e) => { e.preventDefault(); editLeave(leaveSummary); }}>Edit the request</a>
              </p>
            ),
          }]}
        />
      )}

      {batchActivity !== null && (
        <FormModal
          title={`Change activity (${listIds.length})`}
          onCancel={() => setBatchActivity(null)}
          onSubmit={(e) => { e.preventDefault(); const id = batchActivity; setBatchActivity(null); changeActivity(listIds, id); }}
          fields={[{
            label: 'Activity',
            type: 'select',
            value: batchActivity,
            onChange: (e) => setBatchActivity(e.target.value),
            options: [{ value: '', label: 'No activity' }, ...activityTypes.map((t) => ({ value: String(t.activity_type_id), label: t.name }))],
          }]}
        />
      )}

      {showNewActivityTypeModal && (
        <FormModal
          title="Add New Activity"
          onCancel={() => { setShowNewActivityTypeModal(false); setNewActivityTypeForm({ name: '', description: '', color: '#3b82f6' }); }}
          onSubmit={(e) => { e.preventDefault(); handleCreateActivityType(); }}
          fields={[
            {
              label: 'Activity Name',
              type: 'custom',
              render: () => (
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={newActivityTypeForm.name}
                      onChange={(e) => setNewActivityTypeForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter activity name..."
                      style={{ width: '100%', padding: '14px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', backgroundColor: 'white' }}
                      required
                    />
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Color</label>
                    <ColorPicker value={newActivityTypeForm.color} onChange={(e) => setNewActivityTypeForm((prev) => ({ ...prev, color: e.target.value }))} />
                  </div>
                </div>
              ),
            },
            { label: 'Description', type: 'textarea', value: newActivityTypeForm.description, onChange: (e) => setNewActivityTypeForm((prev) => ({ ...prev, description: e.target.value })), placeholder: 'Enter description (optional)...', required: false },
          ]}
        />
      )}

      {showAddDetail && (
        <CustomFieldModal defaultEntity='booking' onClose={() => setShowAddDetail(false)} onSaved={loadBookingFields} onMessage={setToast} />
      )}

      {showManageActivities && (
        <FormModal
          title="Manage activities"
          cancelLabel="Done"
          onCancel={async () => { setShowManageActivities(false); setActivityTypes(await getActivityTypes()); }}
          fields={[{
            type: 'custom',
            render: () => (
              <EntityListManager
                onMessage={setToast}
                title="Activities"
                description="Activities say what the time on a booking is for."
                entityNoun="Activity"
                entityNounPlural="Activities"
                idKey="activity_type_id"
                namePlaceholder="e.g. Design, Development, Meetings"
                load={() => getActivityTypeList().then((d) => d.activityTypes || d)}
                create={createActivityType}
                update={updateActivityType}
                remove={deleteActivityType}
                deleteWarning={(item) => `Delete the "${item.name}" activity? Bookings using it keep their dates but lose the activity.`}
              />
            ),
          }]}
        />
      )}

      {showVisibilityModal && (
        <FormModal
          title="Who appears on the schedule"
          onCancel={() => setShowVisibilityModal(false)}
          onSubmit={(e) => { e.preventDefault(); setShowVisibilityModal(false); }}
          fields={[{
            type: 'custom',
            render: () => (
              <div style={{ maxHeight: '360px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {isVisibilityLoading ? (
                  <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading employees...</div>
                ) : visibilityEmployees.length === 0 ? (
                  <div style={{ color: '#64748b', fontSize: '0.9rem' }}>No employees found.</div>
                ) : (
                  visibilityEmployees.map((employee) => (
                    <label key={employee.employee_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                      <div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1e293b' }}>{employee.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{employee.job_title || 'No title'}</div>
                      </div>
                      <input type="checkbox" checked={!employee.hide_on_schedule} onChange={(e) => toggleVisibility(employee.employee_id, !e.target.checked)} />
                    </label>
                  ))
                )}
              </div>
            ),
          }]}
        />
      )}
    </div>
  );
};

export default Schedule;
